import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, uploadFile } from '../../shared/api';
import { DateInput } from '../../shared/DateInput';
import { formatDate } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%', boxSizing: 'border-box' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type RequestRow = {
    id: number;
    public_id: string;
    employee_id: number;
    department_id: number | null;
    type: string;
    title: string;
    details_json: string | null;
    date_from: string | null;
    date_to: string | null;
    budget_amount: string | number | null;
    receipt_path: string | null;
    created_item_code: string | null;
    status: string;
    note: string | null;
    created_at: string;
    first_name: string | null;
    last_name: string | null;
    department_name: string | null;
    approved_by_name: string | null;
    created_by_name: string | null;
};

const TYPE_OPTIONS: { value: string; hr: string; en: string }[] = [
    { value: 'godisnji', hr: 'Godišnji odmor', en: 'Annual leave' },
    { value: 'bolovanje', hr: 'Bolovanje', en: 'Sick leave' },
    { value: 'pismo_namjere', hr: 'Pismo namjere', en: 'Letter of intent' },
    { value: 'ugovor_o_radu', hr: 'Ugovor o radu', en: 'Employment contract' },
    { value: 'platna_lista', hr: 'Platna lista', en: 'Payslip' },
    { value: 'revers', hr: 'Revers (oprema)', en: 'Equipment release' },
    { value: 'oprema', hr: 'Nabava opreme', en: 'Equipment request' },
];

const STATUS_LABEL: Record<string, { hr: string; en: string; color: string }> = {
    pending: { hr: 'Na čekanju', en: 'Pending', color: '#a6791f' },
    approved: { hr: 'Odobreno', en: 'Approved', color: '#2f8f4e' },
    executed: { hr: 'Izvršeno', en: 'Executed', color: '#3a6fb0' },
    completed: { hr: 'Dovršeno', en: 'Completed', color: '#5a5a5a' },
    rejected: { hr: 'Odbijeno', en: 'Rejected', color: '#b14040' },
};

function typeLabel(v: string, locale: Locale): string {
    const o = TYPE_OPTIONS.find((x) => x.value === v);
    return o ? (locale === 'en' ? o.en : o.hr) : v;
}

function StatusBadge({ status, locale }: { status: string; locale: Locale }) {
    const s = STATUS_LABEL[status] ?? { hr: status, en: status, color: '#777' };
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: s.color }}>{locale === 'en' ? s.en : s.hr}</span>;
}

export function RequestsTab({ token, locale, user }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isSuper = user.role === 'superadministrator';
    const level = user.hr_access_level ?? (isSuper ? 'hr_manager' : null);
    const isManager = isSuper || level !== null; // hr_manager ili business_manager → red odobravanja

    type Mode = 'mine' | 'queue';
    const [mode, setMode] = useState<Mode>(isManager ? 'queue' : 'mine');

    return (
        <div>
            {isManager ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <button type="button" style={tabBtn(mode === 'queue')} onClick={() => setMode('queue')}>{t('Red odobravanja', 'Approval queue')}</button>
                    <button type="button" style={tabBtn(mode === 'mine')} onClick={() => setMode('mine')}>{t('Moji zahtjevi', 'My requests')}</button>
                </div>
            ) : null}

            {mode === 'queue' && isManager ? <ApprovalQueue token={token} locale={locale} /> : <MyRequests token={token} locale={locale} />}
        </div>
    );
}

// ── Moji zahtjevi (self-service) ───────────────────────────────────────────────
function MyRequests({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<RequestRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);

    const load = useCallback(() => {
        setRows(null);
        setErr(null);
        api<{ requests: RequestRow[] }>('/hr/requests/mine', token)
            .then((r) => setRows(r.requests))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token]);
    useEffect(load, [load]);

    return (
        <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ color: BROWN }}>{t('Moji zahtjevi', 'My requests')} {rows ? `(${rows.length})` : ''}</strong>
                <button type="button" style={tabBtn(true)} onClick={() => setShowForm((v) => !v)}>＋ {t('Novi zahtjev', 'New request')}</button>
            </div>

            {showForm ? <NewRequestForm token={token} locale={locale} onDone={() => { setShowForm(false); load(); }} /> : null}

            {err ? <div style={{ color: '#b14040', marginBottom: 8 }}>{err}</div> : null}
            {rows === null ? <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
                : rows.length === 0 ? <div className="muted">{t('Nemaš zahtjeva.', 'No requests yet.')}</div>
                : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Tip', 'Type')}</th>
                                <th style={th}>{t('Naslov', 'Title')}</th>
                                <th style={th}>{t('Period', 'Period')}</th>
                                <th style={th}>{t('Status', 'Status')}</th>
                                <th style={th}>{t('Račun', 'Receipt')}</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td style={td}>{typeLabel(r.type, locale)}</td>
                                        <td style={td}>{r.title}</td>
                                        <td style={td}>{r.date_from ? formatDate(r.date_from, locale) : '—'}{r.date_to ? ' – ' + formatDate(r.date_to, locale) : ''}</td>
                                        <td style={td}><StatusBadge status={r.status} locale={locale} /></td>
                                        <td style={td}><ReceiptCell row={r} token={token} locale={locale} onDone={load} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </div>
    );
}

function ReceiptCell({ row, token, locale, onDone }: { row: RequestRow; token: string; locale: Locale; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const needsReceipt = row.type === 'revers' && row.budget_amount != null && Number(row.budget_amount) > 0;

    if (!needsReceipt) return <span className="muted">—</span>;
    if (row.receipt_path) return <span style={{ color: '#2f8f4e' }}>✓ {t('priložen', 'attached')}</span>;

    const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        setErr(null);
        try {
            const fd = new FormData();
            fd.append('receipt', f);
            await uploadFile(`/hr/requests/${row.id}/receipt`, token, fd);
            onDone();
        } catch (er) {
            setErr(String((er as Error)?.message ?? er));
        } finally {
            setBusy(false);
        }
    };

    return (
        <span>
            <label style={{ ...tabBtn(false), display: 'inline-block', padding: '4px 10px', cursor: 'pointer' }}>
                {busy ? t('Slanje…', 'Uploading…') : t('Priloži račun', 'Upload receipt')}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }} onChange={onPick} disabled={busy} />
            </label>
            {err ? <div style={{ color: '#b14040', fontSize: '0.74rem' }}>{err}</div> : null}
        </span>
    );
}

function NewRequestForm({ token, locale, onDone }: { token: string; locale: Locale; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [type, setType] = useState('godisnji');
    const [title, setTitle] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [budget, setBudget] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const isRevers = type === 'revers' || type === 'oprema';
    const isLeave = type === 'godisnji' || type === 'bolovanje';

    const submit = async () => {
        if (!title.trim()) { setErr(t('Naslov je obavezan.', 'Title is required.')); return; }
        setBusy(true);
        setErr(null);
        try {
            const body: Record<string, unknown> = { type, title: title.trim() };
            if (dateFrom) body.date_from = dateFrom;
            if (dateTo) body.date_to = dateTo;
            if (isRevers && budget) body.budget_amount = Number(budget);
            if (note.trim()) body.note = note.trim();
            await mutate('/hr/requests', token, 'POST', body);
            onDone();
        } catch (e) {
            setErr(String((e as Error)?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ border: '1px dashed #d8cdb2', borderRadius: 10, padding: 14, marginBottom: 14, background: '#fffdf6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Tip zahtjeva', 'Request type')}
                    <select style={inp} value={type} onChange={(e) => setType(e.target.value)}>
                        {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{locale === 'en' ? o.en : o.hr}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Naslov', 'Title')}
                    <input style={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('Kratak opis', 'Short description')} />
                </label>
                {isLeave ? (
                    <>
                        <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Od', 'From')}
                            <DateInput value={dateFrom} onChange={setDateFrom} />
                        </label>
                        <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Do', 'To')}
                            <DateInput value={dateTo} onChange={setDateTo} />
                        </label>
                    </>
                ) : null}
                {isRevers ? (
                    <label style={{ fontSize: '0.78rem', color: BROWN }}>{t('Budžet (EUR)', 'Budget (EUR)')}
                        <input style={inp} type="number" min="0" step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} />
                    </label>
                ) : null}
            </div>
            <label style={{ fontSize: '0.78rem', color: BROWN, display: 'block', marginTop: 10 }}>{t('Napomena', 'Note')}
                <textarea style={{ ...inp, minHeight: 60 }} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            {isRevers ? <div className="muted" style={{ fontSize: '0.74rem', marginTop: 6 }}>{t('Za revers s budžetom račun se prilaže nakon nabave.', 'For a release with budget, attach the receipt after purchase.')}</div> : null}
            {err ? <div style={{ color: '#b14040', marginTop: 8 }}>{err}</div> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" style={tabBtn(true)} disabled={busy} onClick={submit}>{busy ? t('Slanje…', 'Submitting…') : t('Pošalji', 'Submit')}</button>
                <button type="button" style={tabBtn(false)} disabled={busy} onClick={onDone}>{t('Odustani', 'Cancel')}</button>
            </div>
        </div>
    );
}

// ── Red odobravanja (manager) ───────────────────────────────────────────────────
function ApprovalQueue({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<RequestRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [busyId, setBusyId] = useState<number | null>(null);

    const load = useCallback(() => {
        setRows(null);
        setErr(null);
        const qs = statusFilter ? `?status=${statusFilter}` : '';
        api<{ requests: RequestRow[] }>(`/hr/requests${qs}`, token)
            .then((r) => setRows(r.requests))
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token, statusFilter]);
    useEffect(load, [load]);

    const act = async (id: number, action: 'approve' | 'reject' | 'execute' | 'complete') => {
        setBusyId(id);
        setErr(null);
        try {
            await mutate(`/hr/requests/${id}/${action}`, token, 'POST', {});
            load();
        } catch (e) {
            setErr(String((e as Error)?.message ?? e));
        } finally {
            setBusyId(null);
        }
    };

    const filters = useMemo(() => ['', 'pending', 'approved', 'executed', 'completed', 'rejected'], []);

    return (
        <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <strong style={{ color: BROWN }}>{t('Red odobravanja', 'Approval queue')} {rows ? `(${rows.length})` : ''}</strong>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {filters.map((f) => (
                        <button key={f || 'all'} type="button" style={tabBtn(statusFilter === f)} onClick={() => setStatusFilter(f)}>
                            {f === '' ? t('Sve', 'All') : (STATUS_LABEL[f] ? (locale === 'en' ? STATUS_LABEL[f].en : STATUS_LABEL[f].hr) : f)}
                        </button>
                    ))}
                </div>
            </div>

            {err ? <div style={{ color: '#b14040', marginBottom: 8 }}>{err}</div> : null}
            {rows === null ? <div className="muted">{t('Učitavanje…', 'Loading…')}</div>
                : rows.length === 0 ? <div className="muted">{t('Nema zahtjeva.', 'No requests.')}</div>
                : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Djelatnik', 'Employee')}</th>
                                <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                <th style={th}>{t('Tip', 'Type')}</th>
                                <th style={th}>{t('Naslov', 'Title')}</th>
                                <th style={th}>{t('Period', 'Period')}</th>
                                <th style={th}>{t('Budžet', 'Budget')}</th>
                                <th style={th}>{t('Status', 'Status')}</th>
                                <th style={th}>{t('Akcije', 'Actions')}</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => {
                                    const busy = busyId === r.id;
                                    return (
                                        <tr key={r.id}>
                                            <td style={td}>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                                            <td style={td}>{r.department_name ?? '—'}</td>
                                            <td style={td}>{typeLabel(r.type, locale)}</td>
                                            <td style={td}>{r.title}</td>
                                            <td style={td}>{r.date_from ? formatDate(r.date_from, locale) : '—'}{r.date_to ? ' – ' + formatDate(r.date_to, locale) : ''}</td>
                                            <td style={td}>{r.budget_amount != null ? Number(r.budget_amount).toLocaleString('hr-HR', { minimumFractionDigits: 2 }) + ' €' : '—'}</td>
                                            <td style={td}><StatusBadge status={r.status} locale={locale} /></td>
                                            <td style={td}>
                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                    {r.status === 'pending' ? (
                                                        <>
                                                            <button type="button" style={{ ...tabBtn(true), padding: '4px 10px' }} disabled={busy} onClick={() => act(r.id, 'approve')}>{t('Odobri', 'Approve')}</button>
                                                            <button type="button" style={{ ...tabBtn(false), padding: '4px 10px', color: '#b14040' }} disabled={busy} onClick={() => act(r.id, 'reject')}>{t('Odbij', 'Reject')}</button>
                                                        </>
                                                    ) : null}
                                                    {(r.status === 'pending' || r.status === 'approved') ? (
                                                        <button type="button" style={{ ...tabBtn(false), padding: '4px 10px' }} disabled={busy} onClick={() => act(r.id, 'execute')}>{t('Izvrši', 'Execute')}</button>
                                                    ) : null}
                                                    {(r.status === 'approved' || r.status === 'executed') ? (
                                                        <button type="button" style={{ ...tabBtn(false), padding: '4px 10px', borderColor: GOLD, color: GOLD }} disabled={busy} onClick={() => act(r.id, 'complete')}>{t('Dovrši', 'Complete')}</button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
        </div>
    );
}
